import { format } from "@/util/date-util";
import { getRouterLink } from "@/util/helpers";
import { setNoteFromRequest } from "@/models/Note";
import { TAX_OPTION } from "@/models/Case";

export const QUOTE_REQUEST_LINE = {
  LIFE: "life",
  LTC: "ltc",
  DISABILITY: "disability",
  ANNUITY: "annuity",
  GROUP: "group"
};

export const QUOTE_LINES = [
  { title: "Annuity", value: "annuity" },
  { title: "Disability", value: "disability" },
  { title: "Group", value: "group" },
  { title: "Life", value: "life" },
  { title: "LTC", value: "ltc" },
  { title: "Linked Benefit", value: "linked_benefit" },
  { title: "Variable Life", value: "variable_life" }
];

export const TAX_OPTIONS = [
  TAX_OPTION._401K,
  TAX_OPTION._403B,
  TAX_OPTION.INHERITED_IRA_PLAN,
  TAX_OPTION.IRA,
  TAX_OPTION.NON_QUALIFIED,
  TAX_OPTION.PENSION_PLAN,
  TAX_OPTION.PROFIT_SHARING_PLAN,
  TAX_OPTION.ROTH_IRA,
  TAX_OPTION.SEP_IRA,
  TAX_OPTION.SIMPLE_IRA
];

export const EMPLOYEE_COUNTS = [
  "None",
  "1-10 Employees",
  "11-50 Employees",
  "51+ Employees"
];

export const STATUSES = [
  "Entered",
  "Pending",
  "Awaiting More Information from Agent",
  "Refer to Advanced Planning",
  "Refer to Marketing Manager",
  "Refer to Internal Wholesaler",
  "Awaiting Application",
  "Received Application",
  "No Sale",
  "Awaiting Policy Review",
  "Policy Review Complete"
];

export const MODE = {
  ANNUAL: "Annual",
  SEMI_ANNUAL: "Semi-Annual",
  QUARTERLY: "Quarterly",
  MONTHLY: "Monthly"
};

export const MODES = Object.values(MODE);

export const TERM_SUBTYPES = [
  { value: "10 Year Term", title: "10 Year" },
  { value: "15 Year Term", title: "15 Year" },
  { value: "20 Year Term", title: "20 Year" },
  { value: "25 Year Term", title: "25 Year" },
  { value: "30 Year Term", title: "30 Year" },
  { value: "35 Year Term", title: "35 Year" },
  { value: "40 Year Term", title: "40 Year" }
];

export const GUL_SUBTYPES = [
  { value: "GUL to Age 90", title: "To Age 90" },
  { value: "GUL to Age 95", title: "To Age 95" },
  { value: "GUL to Age 100", title: "To Age 100" },
  { value: "GUL to Age 105", title: "To Age 105" },
  { value: "GUL to Age 120", title: "To Age 120" }
];

export const QA_LINES = [QUOTE_REQUEST_LINE.LIFE, QUOTE_REQUEST_LINE.LTC];
export const QA_UNDERWRITING_CLASSES = [
  "Standard Plus",
  "Preferred NT",
  "Super Preferred",
  "Standard NT",
  "Preferred Tobacco",
  "Standard Tobacco",
  "Level",
  "Graded",
  "Level Tobacco",
  "Declined"
];

export const HELPER_TYPES = {
  TERM: "_TERM",
  GUL: "_GUL"
};

export const INFLATION_ITEMS = [
  { title: "None", value: null },
  { title: "Deferred", value: "Deferred" },
  { title: "1% Compound", value: "1% Compound" },
  { title: "2% Compound", value: "2% Compound" },
  { title: "3% Simple", value: "3% Simple" },
  { title: "3% Compound", value: "3% Compound" },
  { title: "3% Compound for 20 Years", value: "3% Compound for 20 Years" },
  { title: "4% Compound", value: "4% Compound" },
  { title: "5% Compound", value: "5% Compound" },
  { title: "5% Compound for 20 Years", value: "5% Compound for 20 Years" },
  { title: "5% Compound Limit 2X", value: "5% Compound Limit 2X" },
  { title: "5% Simple", value: "5% Simple" },
  { title: "5% Step Rated", value: "5% Step Rated" },
  { title: "6% Compound", value: "6% Compound" },
  { title: "CPI", value: "CPI" },
  { title: "US Medical Care Inflation", value: "US Medical Care Inflation" }
];

export const DISCOUNT_ITEMS = [
  { title: "None", value: null },
  { title: "Association", value: "Association" },
  { title: "Employer", value: "Employer" },
  { title: "Producer", value: "Producer" }
];

export const BENEFIT_PERIODS = [
  "1 Year",
  "2 Years",
  "3 Years",
  "4 Years",
  "5 Years",
  "6 Years",
  "7 Years",
  "8 Years",
  "9 Years",
  "10 Years",
  "Max"
];

export const MONTHLY_BENEFIT_SOLVE_ITEMS = ["Max", "Specify Amount"];

export const DISABILITY_BENEFIT_PERIODS = [
  { title: "Lump Sum", value: "Lump Sum" },
  { title: "1 Year", value: "1 Year" },
  { title: "18 Months", value: "18 Months" },
  { title: "2 Years", value: "2 Years" },
  { title: "5 Years", value: "5 Years" },
  { title: "10 Years", value: "10 Years" },
  { title: "To Age 65", value: "To Age 65" },
  { title: "To Age 67 (recommended)", value: "To Age 67" },
  { title: "To Age 70", value: "To Age 70" }
];

export const DISABILITY_TYPE_ITEMS = [
  "Business Overhead",
  "Buy Sell",
  "Individual Disability",
  "Key Person"
];

export const TOBACCO_USE_ITEMS = [
  { title: "Non-Tobacco", value: false },
  { title: "Tobacco Use", value: true }
];

export const SHARED_CARE_ITEMS = [
  { title: "Yes", value: true },
  { title: "No", value: false }
];

export const QUOTE_REQUEST_LINES = Object.values(QUOTE_REQUEST_LINE);

export const MARITAL_STATUS_ITEMS = ["Married", "Single", "Domestic Parter"];

const SURRENDER_PERIODS = [];
for (let i = 0; i < 10; i++) {
  let title = `${i + 1} Years`;
  if (i === 0) title = `${i + 1} Year`;
  SURRENDER_PERIODS.push({ value: i + 1, title });
}
export { SURRENDER_PERIODS };

export const INCOME_DURATIONS = {
  LIFE: "Life",
  CERTAIN_ONLY: "Certain Only"
};
export const INCOME_DURATION_ITEMS = Object.values(INCOME_DURATIONS);
export const REFUND_OPTIONS = {
  LIFE_ONLY: "Life Only",
  CERTAIN_PERIOD: "Certain Period",
  LIFE_WITH_INSTALLMENT_REFUND: "Installment Refund",
  LIFE_WITH_CASH_REFUND: "Cash Refund"
};
export const REFUND_OPTION_ITEMS = Object.values(REFUND_OPTIONS);

export const generatePayDurations = currentAge => {
  const payDurations = [{ title: "Life", value: -1 }];
  for (let i = currentAge; i <= 120; i++) {
    let delta = i - currentAge;
    let title = `${delta} Years / Age ${i}`;
    if (delta === 1) title = `${delta} Year / Age ${i}`;
    payDurations.push({ title, value: delta });
  }
  return payDurations;
};

export const QUOTE_ROLES = {
  ANNUITANT: "Annuitant",
  JOINT_ANNUITANT: "Joint Annuitant",
  INSURED: "Insured",
  JOINT_INSURED: "Joint Insured"
};

export function Quote(model = {}) {
  return {
    agent: model?.agent || null,
    annuityGuaranteeType: model?.annuityGuaranteeType || null,
    approvedDomainUrl: model?.approvedDomainUrl || null,
    assignable: model?.assignable || { id: null, type: null, name: null },
    benefitPeriod: model?.benefitPeriod || null,
    businessOwner: model?.businessOwner || null,
    case: model?.case || null,
    certainPeriod: model?.certainPeriod || null,
    concept: model?.concept || null,
    createdAt: model?.createdAt || null,
    description: model?.description || null,
    discount: model?.discount || null,
    eapp: model?.eapp || null,
    eliminationPeriod: model?.eliminationPeriod || null,
    employeeCount: model?.employeeCount || null,
    existingBenefitMaximum: model?.existingBenefitMaximum || null,
    existingBenefitPeriod: model?.existingBenefitPeriod || null,
    existingCoverage: model?.existingCoverage || null,
    existingEliminationPeriod: model?.existingEliminationPeriod || null,
    existingEmployerPaid: model?.existingEmployerPaid || null,
    existingPercentageMaximum: model?.existingPercentageMaximum || null,
    faceAmount: model?.faceAmount || null,
    files: model?.files || [], // NOTE(tim): only used during creation
    followUp: model?.followUp || null,
    id: model?.id || null,
    impairedRiskQuotes: model?.impairedRiskQuotes || [],
    incomeDurationType: model?.incomeDurationType || null,
    incomeEnabled: model?.incomeEnabled || false,
    incomeEndAge: model?.incomeEndAge || null,
    incomeStartAge: model?.incomeStartAge || null,
    incomeStartDate: model?.incomeStartDate || null,
    inflation: model?.inflation || null,
    insured: model?.insured || new QuoteRole(),
    jointInsured: model?.jointInsured || new QuoteRole(),
    line: model?.line || null,
    mode: model?.mode || null,
    monthlyBenefit: model?.monthlyBenefit || null,
    monthlyBenefitSolve: model?.monthlyBenefitSolve || null,
    sharedCare: model?.sharedCare || null,
    notes: model?.notes || [],
    quoteAccesses: model?.quoteAccesses || [],
    payDurations: model?.payDurations || [],
    premium: model?.premium || null,
    productType: model?.productType || [],
    qualifiedPlanBalance: model?.qualifiedPlanBalance || null,
    quoteRoles: model?.quoteRoles || [],
    riders: model?.riders || null,
    smoker: model?.smoker || null,
    state: model?.state || null,
    status: model?.status || null,
    surrenderPeriods: model?.surrenderPeriods || [],
    termDuration: model?.termDuration || null,
    typeOfFunds: model?.typeOfFunds || null,
    yearsAsOwner: model?.yearsAsOwner || null
  };
}

export function setQuoteFromRequest(raw = {}) {
  const quote = new Quote();
  quote.annuityGuaranteeType = raw?.annuity_guarantee_type;
  quote.approvedDomainUrl = raw?.approved_domain_url;
  quote.benefitPeriod = raw?.benefit_period;
  quote.businessOwner = raw?.business_owner;
  quote.case = raw?.case?.id;
  quote.certainPeriod = raw?.certain_period;
  quote.concept = raw?.concept;
  quote.createdAt = raw?.created_at;
  quote.description = raw?.description;
  quote.discount = raw?.discount;
  quote.eapp = raw?.electronic_application?.id;
  quote.eliminationPeriod = raw?.elimination_period;
  quote.employeeCount = raw?.employee_count;
  quote.existingBenefitMaximum = raw?.existing_benefit_maximum;
  quote.existingBenefitPeriod = raw?.existing_benefit_period;
  quote.existingCoverage = raw?.existing_coverage;
  quote.existingEliminationPeriod = raw?.existing_elimination_period;
  quote.existingEmployerPaid = raw?.existing_employer_paid;
  quote.existingPercentageMaximum = raw?.existing_percentage_maximum;
  quote.faceAmount = raw?.face_amount;
  quote.id = raw?.id;
  quote.incomeDurationType = raw?.income_duration_type;
  quote.incomeEndAge = raw?.income_end_age;
  quote.incomeStartAge = raw?.income_start_age;
  quote.incomeStartDate = raw?.income_start_date;
  quote.inflation = raw?.inflation;
  quote.insured = raw?.insured?.name;
  quote.line = raw?.line;
  quote.mode = raw?.mode;
  quote.monthlyBenefit = raw?.monthly_benefit;
  quote.monthlyBenefitSolve = raw?.monthly_benefit_type;
  quote.sharedCare = raw?.shared_care;
  quote.premium = raw?.premium;
  quote.qualifiedPlanBalance = raw?.qualified_plan_balance;
  quote.riders = raw?.riders;
  quote.state = raw?.state;
  quote.status = raw?.status;
  quote.termDuration = raw?.term_duration;
  quote.typeOfFunds = raw?.type_of_funds;
  quote.yearsAsOwner = raw?.years_as_owner;

  if (
    quote.incomeStartAge ||
    quote.incomeStartAge === 0 ||
    quote.incomeEndAge ||
    quote.incomeEndAge === 0
  ) {
    quote.incomeEnabled = true;
  }
  let productType = raw?.product_type;

  if (typeof productType === "string") {
    try {
      productType = JSON.parse(productType);
      quote.productType = productType;
      const termValues = TERM_SUBTYPES.map(v => v.value);
      if (productType.some(v => termValues.includes(v))) {
        productType.push(HELPER_TYPES.TERM);
      }

      const gulValues = GUL_SUBTYPES.map(v => v.value);
      if (productType.some(v => gulValues.includes(v))) {
        productType.push(HELPER_TYPES.GUL);
      }
    } catch (e) {
      // do nothing
    }
  }

  let surrenderPeriods = raw?.surrender_periods;

  if (typeof surrenderPeriods === "string") {
    try {
      surrenderPeriods = JSON.parse(surrenderPeriods);
      quote.surrenderPeriods = surrenderPeriods;
    } catch (e) {
      // do nothing
    }
  }

  let payDurations = raw?.pay_durations;

  if (typeof payDurations === "string") {
    try {
      payDurations = JSON.parse(payDurations);
      quote.payDurations = payDurations;
    } catch (e) {
      // do nothing
    }
  }

  if (raw?.follow_up_at) {
    try {
      quote.followUp = format(new Date(raw.follow_up_at), "yyyy-MM-dd");
    } catch (e) {
      // do something?
    }
  }

  if (raw?.assignable) {
    quote.assignable = {
      id: raw.assignable.id,
      name: raw.assignable.name,
      type: raw.assignable.type.raw
    };
  }

  quote.impairedRiskQuotes = [];
  if (Array.isArray(raw?.quick_quotes))
    quote.impairedRiskQuotes = raw.quick_quotes.map(
      setQuoteImpairedRiskQuoteFromRequest
    );

  quote.notes = raw?.notes?.map(setNoteFromRequest);

  quote.quoteAccesses = raw?.quote_accesses?.map(setQuoteRoomAccessFromRequest);

  quote.quoteRoles = raw?.quote_roles?.map(setQuoteRoleFromRequest);

  return quote;
}

function QuoteImpairedRiskQuote(model = {}) {
  return {
    id: model?.id,
    status: model?.status,
    createdAt: model?.createdAt
  };
}

function setQuoteImpairedRiskQuoteFromRequest(raw = {}) {
  const model = QuoteImpairedRiskQuote();
  model.id = raw?.id;
  model.status = raw?.status;
  model.createdAt = raw?.created_at;
  return model;
}

function QuoteRole(model = {}) {
  return {
    id: model?.id || null,
    ownable: {
      id: model?.ownable?.id || null,
      name: model?.ownable?.name || null,
      firstName: model?.ownable?.firstName || null,
      lastName: model?.ownable?.lastName || null,
      gender: model?.ownable?.gender || null,
      birthdate: model?.ownable?.birthdate || null,
      maritalStatus: model?.ownable?.maritalStatus || null,
      income:
        model?.ownable?.income || model?.ownable?.income === 0
          ? model?.ownable?.income
          : null,
      tobacco: Boolean(model?.ownable?.tobacco)
    },
    address: {
      street_address: model?.address?.street_address || null,
      city: model?.address?.city || null,
      state: model?.address?.state || null,
      zip: model?.address?.zip || null
    },
    occupationAndDuties: null,
    role: model?.role || null,
    underwritingClass: model?.underwritingClass || null,
    qualifiedPlanBalance: model?.qualifiedPlanBalance || null
  };
}

function setQuoteRoleFromRequest(model = {}) {
  const quoteRole = QuoteRole();

  quoteRole.id = model?.id;
  quoteRole.role = model?.role;
  quoteRole.underwritingClass = model?.underwriting_class;
  quoteRole.qualifiedPlanBalance = model?.qualified_plan_balance;

  quoteRole.ownable.type = model?.ownable?.type;
  quoteRole.ownable.id = model?.ownable?.id;
  quoteRole.ownable.name = model?.ownable?.name;
  quoteRole.ownable.firstName = model?.ownable?.first_name;
  quoteRole.ownable.lastName = model?.ownable?.last_name;
  quoteRole.ownable.gender = model?.ownable?.gender;
  quoteRole.ownable.birthdate = model?.ownable?.birthdate;
  quoteRole.ownable.maritalStatus = model?.ownable?.marital_status;
  quoteRole.ownable.income = model?.ownable?.income;

  return quoteRole;
}

export function QuoteRoomAccess(model = {}) {
  return {
    agent: model?.agent || null,
    disabled: model?.disabled || null,
    email: model?.email || null,
    id: model?.id || null,
    owner: model?.owner || null,
    name: model?.name || "",
    ownerId: model?.ownerId || null,
    ownerType: model?.ownerType || null,
    routerLink: model?.routerLink || null
  };
}

export function setQuoteRoomAccessFromRequest(model = {}) {
  const access = QuoteRoomAccess();
  access.agent = model?.agent;
  access.disabled = model?.disabled;
  access.email = model?.email;
  access.id = model?.id;
  access.owner = model?.owner;
  access.ownerId = model?.ownable?.id;
  access.name = model?.ownable?.name;
  access.ownerType = model?.ownable?.type?.model;

  if (access.ownerId && access.ownerType) {
    access.routerLink = getRouterLink(access.ownerType, access.ownerId);
  }

  return access;
}

export function QuoteAttributeToRequest(model, attribute) {
  const requestMap = {
    monthlyBenefitSolve: "monthly_benefit_solve",
    monthlyBenefit: "monthly_benefit",
    riders: "riders",
    existingBenefitMaximum: "existing_benefit_maximum",
    existingBenefitPeriod: "existing_benefit_period",
    existingCoverage: "existing_coverage",
    existingEliminationPeriod: "existing_elimination_period",
    existingEmployerPaid: "existing_employer_paid",
    existingPercentageMaximum: "existing_percentage_maximum",
    benefitPeriod: "benefit_period",
    eliminationPeriod: "elimination_period",
    mode: "mode",
    smoker: "smoker",
    status: "status",
    termDuration: "term_duration",
    state: "state",
    faceAmount: "face_amount",
    premium: "premium",
    description: "description",
    inflation: "inflation",
    discount: "discount",
    files: "documents_attributes",
    typeOfFunds: "type_of_funds",
    productType: "product_type",
    followUp: "follow_up_at",
    assignable: "assignable",
    sharedCare: "shared_care",
    surrenderPeriods: "surrender_periods",
    incomeDurationType: "income_duration_type",
    annuityGuaranteeType: "annuity_guarantee_type",
    certainPeriod: "certain_period",
    incomeStartDate: "income_start_date",
    incomeStartAge: "income_start_age",
    incomeEndAge: "income_end_age",
    payDurations: "pay_durations",
    businessOwner: "business_owner",
    employeeCount: "employee_count",
    yearsAsOwner: "years_as_owner"
  };

  let value = model[attribute];
  if (attribute === "assignable") {
    value = {
      assignable_type: model.assignable?.type || null,
      assignable_id: model.assignable?.id || null
    };
  } else if (["payDurations", "surrenderPeriods"].includes(attribute)) {
    value = JSON.stringify(value);
  } else if (attribute === "productType") {
    value = productTypeToRequestValue(value);
  }
  return { [requestMap[attribute]]: value };
}

export function productTypeToRequestValue(productType) {
  const helperTypes = Object.values(HELPER_TYPES);
  const value = productType.filter(v => !helperTypes.includes(v));
  return JSON.stringify(value);
}

export function QuoteRequestAnnuityInfo(model = {}) {
  return {
    typeOfFunds: model.typeOfFunds,
    productType: model.productType,
    premium: model.premium,
    state: model.state,
    status: model.status,
    surrenderPeriods: model.surrenderPeriods,
    incomeDurationType: model.incomeDurationType,
    incomeStartAge: model.incomeStartAge,
    incomeEndAge: model.incomeEndAge,
    annuityGuaranteeType: model.annuityGuaranteeType,
    certainPeriod: model.certainPeriod,
    incomeStartDate: model.incomeStartDate
  };
}

export function QuoteRequestDisabilityInfo(model = {}) {
  return {
    benefitPeriod: model.benefitPeriod,
    businessOwner: model.businessOwner,
    eliminationPeriod: model.eliminationPeriod,
    employeeCount: model.employeeCount,
    existingBenefitMaximum: model.existingBenefitMaximum,
    existingBenefitPeriod: model.existingBenefitPeriod,
    existingCoverage: model.existingCoverage,
    existingEliminationPeriod: model.existingEliminationPeriod,
    existingEmployerPaid: model.existingEmployerPaid,
    existingPercentageMaximum: model.existingPercentageMaximum,
    mode: model.mode,
    monthlyBenefit: model.monthlyBenefit,
    monthlyBenefitSolve: model.monthlyBenefitSolve,
    productType: model.productType,
    riders: model.riders,
    smoker: model.smoker,
    state: model.state,
    status: model.status,
    yearsAsOwner: model.yearsAsOwner
  };
}

export function QuoteRequestGroupInfo(model = {}) {
  return {
    status: model.status,
    productType: model.productType
  };
}

export function QuoteRequestLifeInfo(model = {}) {
  return {
    faceAmount: model.faceAmount,
    incomeEnabled: model.incomeEnabled,
    incomeEndAge: model.incomeEndAge,
    incomeStartAge: model.incomeStartAge,
    mode: model.mode,
    payDurations: model.payDurations,
    premium: model.premium,
    productType: model.productType,
    state: model.state,
    status: model.status
  };
}

export function QuoteRequestLtcInfo(model = {}) {
  return {
    state: model.state,
    mode: model.mode,
    monthlyBenefit: model.monthlyBenefit,
    benefitPeriod: model.benefitPeriod,
    eliminationPeriod: model.eliminationPeriod,
    inflation: model.inflation,
    discount: model.discount,
    status: model.status,
    sharedCare: model.sharedCare || false
  };
}

export function QuoteRequestProductTypeData(model = {}) {
  const numOrNull = v => (v || v === 0 ? v : null);
  return {
    enabled: model?.enabled || false,
    productTypes: model?.productTypes || [],
    payDurations: model?.payDurations || [],
    incomeEnabled: model?.incomeEnabled || false,
    incomeStartAge: numOrNull(model?.incomeStartAge),
    incomeEndAge: numOrNull(model?.incomeEndAge),
    incomeDurationType: numOrNull(model?.incomeDurationType),
    incomeStartDate: model?.incomeStartDate || null,
    annuityGuaranteeType: model?.annuityGuaranteeType || null,
    certainPeriod: model?.certainPeriod || null,
    surrenderPeriods: model?.surrenderPeriods || [],
    soleIncomeStartAge: numOrNull(model?.soleIncomeStartAge)
  };
}
